import React, { useState, useEffect } from 'react';
import { useAuthStore, ROLES, hasMinimumRole } from '../lib/auth.js';
import { venueAPI } from '../lib/api.js';
import { supabase } from '../supabaseClient.js';
import { 
  Upload, 
  FileText, 
  ShieldCheck, 
  Loader2, 
  Clock,
  AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button.jsx';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card.jsx';
import { Label } from '../components/ui/label.jsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select.jsx';
import { toast } from 'sonner';

const DOCUMENT_TYPES = [
  'Standard Operating Procedure (SOP)',
  'Risk Assessment',
  'Gas Safety Certificate',
  'EICR Electrical Certificate',
  'Fire Risk Assessment',
  'Pest Control Report',
  'Allergen Matrix'
];

const DocumentVerificationPage = () => {
  const { user } = useAuthStore();
  const [venues, setVenues] = useState([]);
  const [documents, setDocuments] = useState([]);
  
  // Form State
  const [file, setFile] = useState(null);
  const [selectedVenue, setSelectedVenue] = useState('');
  const [docType, setDocType] = useState(DOCUMENT_TYPES[0]);
  const [uploading, setUploading] = useState(false);
  const [loadingDocs, setLoadingDocs] = useState(true);

  useEffect(() => {
    fetchVenues();
    fetchRecentDocuments();
  }, []);

  const fetchVenues = async () => {
    try {
      const response = await venueAPI.list();
      setVenues(response.data);
      if (response.data.length > 0) {
        setSelectedVenue(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch venues:', error);
      toast.error('Could not load venues.');
    }
  };

  const fetchRecentDocuments = async () => {
    setLoadingDocs(true);
    try {
      const { data, error } = await supabase
        .from('compliance_documents')
        .select(`
          id,
          document_type,
          status,
          created_at,
          file_path,
          venues (name)
        `)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      setDocuments(data || []);
    } catch (error) {
      console.error('Error fetching docs:', error);
    } finally {
      setLoadingDocs(false);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file || !selectedVenue || !docType) {
      toast.error('Please select a venue, document type, and a file to upload.');
      return;
    }

    setUploading(true);
    try {
      // 1. Generate unique, collision-proof file path
      const fileExt = file.name.split('.').pop();
      const safeVenueId = selectedVenue.replace(/[^a-zA-Z0-9]/g, '');
      const filePath = `${safeVenueId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

      // 2. Upload directly to Supabase Storage Bucket
      const { error: uploadError } = await supabase.storage
        .from('compliance_docs')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // 3. Create immutable paper trail record
      const { error: dbError } = await supabase
        .from('compliance_documents')
        .insert({
          venue_id: selectedVenue,
          uploaded_by: user.id,
          document_type: docType,
          file_path: filePath,
          status: 'pending_review'
        });

      if (dbError) throw dbError;

      toast.success('Document securely uploaded to EHO Vault');
      setFile(null);
      // Reset file input visually
      document.getElementById('file-upload').value = '';
      fetchRecentDocuments(); // Refresh paper trail

    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload document. Please check network connection.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold font-heading text-[#FF6600] uppercase">Document Verification</h1>
        <p className="text-[#A1A1AA] mt-1">Upload and manage compliance documentation securely.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Upload Interface */}
        <Card className="lg:col-span-1 bg-[#1E1E1E] border-[#3F3F46]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Upload className="w-5 h-5 text-[#FF6600]" />
              Upload Document
            </CardTitle>
            <CardDescription className="text-[#A1A1AA]">
              Files are stored securely with restricted EHO access.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            
            {hasMinimumRole(user, ROLES.OPS) && (
              <div className="space-y-2">
                <Label className="text-[#A1A1AA] font-bold text-xs uppercase">Venue</Label>
                <Select value={selectedVenue} onValueChange={setSelectedVenue}>
                  <SelectTrigger className="w-full bg-[#0A0A0A] border-[#3F3F46] text-white">
                    <SelectValue placeholder="Select Venue" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1E1E1E] border-[#3F3F46]">
                    {venues.map((v) => (
                      <SelectItem key={v.id} value={v.id} className="text-white focus:bg-[#FF6600]/20">
                        {v.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-[#A1A1AA] font-bold text-xs uppercase">Document Type</Label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger className="w-full bg-[#0A0A0A] border-[#3F3F46] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1E1E1E] border-[#3F3F46]">
                  {DOCUMENT_TYPES.map((type) => (
                    <SelectItem key={type} value={type} className="text-white focus:bg-[#FF6600]/20">
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-[#A1A1AA] font-bold text-xs uppercase">Select File</Label>
              <input
                id="file-upload"
                type="file"
                onChange={handleFileChange}
                className="block w-full text-sm text-[#A1A1AA]
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-bold file:uppercase
                  file:bg-[#27272A] file:text-white
                  hover:file:bg-[#3F3F46] file:cursor-pointer
                  cursor-pointer bg-[#0A0A0A] border border-[#3F3F46] rounded-md p-2"
              />
            </div>

            <Button 
              onClick={handleUpload} 
              disabled={uploading || !file}
              className="w-full bg-[#FF6600] hover:bg-[#CC5200] text-white font-bold uppercase mt-4"
            >
              {uploading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading Vault...</>
              ) : (
                <><ShieldCheck className="w-4 h-4 mr-2" /> Secure Upload</>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Paper Trail Log */}
        <Card className="lg:col-span-2 bg-[#1E1E1E] border-[#3F3F46]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#FF6600]" />
              Immutable Paper Trail
            </CardTitle>
            <CardDescription className="text-[#A1A1AA]">
              Recent uploads and compliance verification statuses.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingDocs ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 text-[#FF6600] animate-spin" />
              </div>
            ) : documents.length === 0 ? (
              <div className="text-center py-8 text-[#A1A1AA]">
                <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No documents found in the vault.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {documents.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-3 bg-[#0A0A0A] border border-[#3F3F46] rounded-md">
                    <div>
                      <p className="text-white font-medium text-sm">{doc.document_type}</p>
                      <p className="text-[#52525B] text-xs mt-1 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(doc.created_at).toLocaleDateString()} - {doc.venues?.name || 'Unknown Venue'}
                      </p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-bold uppercase rounded ${
                      doc.status === 'verified' ? 'bg-[#00E676]/20 text-[#00E676]' :
                      doc.status === 'rejected' ? 'bg-[#FF3B30]/20 text-[#FF3B30]' :
                      'bg-[#FFB300]/20 text-[#FFB300]'
                    }`}>
                      {doc.status.replace('_', ' ')}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

      </div>
    </div>
  );
};

export default DocumentVerificationPage;